import { API_BASE_URL } from './apiConfig';

/**
 * Get a secure URL for accessing files through JWT authentication
 * @param {string} filePath - The file path relative to uploads directory
 * @returns {string} - The secure API URL for the file
 */
export const getSecureFileUrl = (filePath) => {
  if (!filePath) return '';
  
  // Clean the file path
  const cleanPath = filePath.replace(/^\/+/, ''); // Remove leading slashes
  
  // Return the secure API endpoint
  return `${API_BASE_URL}/files/${cleanPath}`;
};

/**
 * Open a file in a new tab with authentication
 * @param {string} filePath - The file path relative to uploads directory
 * @param {string} token - JWT token from Redux state
 */
export const openSecureFile = (filePath, token) => {
  if (!token) {
    console.error('No authentication token provided');
    return;
  }
  
  const secureUrl = getSecureFileUrl(filePath);
  
  // Open in new tab with token in URL (for GET requests)
  const urlWithToken = `${secureUrl}?token=${encodeURIComponent(token)}`;
  window.open(urlWithToken, '_blank');
};

/**
 * Download a file securely
 * @param {string} filePath - The file path relative to uploads directory
 * @param {string} token - JWT token from Redux state
 * @param {string} filename - Optional filename for download
 */
export const downloadSecureFile = async (filePath, token, filename = null) => {
  try {
    if (!token) {
      console.error('No authentication token provided');
      return;
    }
    
    const secureUrl = getSecureFileUrl(filePath);
    
    const response = await fetch(secureUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || filePath.split('/').pop();
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
  } catch (error) {
    console.error('Error downloading file:', error);
  }
};

/**
 * Get secure file URL with token for direct use in img src or other elements
 * @param {string} filePath - The file path relative to uploads directory
 * @param {string} token - JWT token from Redux state
 * @returns {string} - The secure URL with token parameter
 */
export const getSecureFileUrlWithToken = (filePath, token) => {
  if (!token) {
    console.error('No authentication token provided');
    return '';
  }
  
  const secureUrl = getSecureFileUrl(filePath);
  return `${secureUrl}?token=${encodeURIComponent(token)}`;
};
